import { useState, type ReactNode } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { NavLink, useLocation } from 'react-router'
import { cockpitMainNavigation, type CockpitNavItem } from '@/features/cockpit/cockpitData'

interface NavigationItem {
  readonly path: string
  readonly label: string
  readonly end: boolean
  readonly badge?: string
}

interface NavigationGroup {
  readonly id: string
  readonly label: string
  readonly items: readonly NavigationItem[]
}

const primaryViews = [
  { path: '/', label: 'Übersicht', end: true },
  {
    path: '/producttype-keywords',
    label: 'Produkttyp-Keywords',
    end: false,
    badge: 'In Arbeit',
  },
  {
    path: '/collection-keywords',
    label: 'Collection-Keywords',
    end: false,
    badge: 'In Arbeit',
  },
  { path: '/topics', label: 'Potenzial-Trichter', end: false, badge: 'Arbeitsfokus' },
] as const satisfies readonly NavigationItem[]

const cockpitViews: readonly CockpitNavItem[] = cockpitMainNavigation.filter(
  (item) => item.path !== '/',
)

const analysisViews = [
  { path: '/stats', label: 'Themen-Statistik', end: true },
  { path: '/orders', label: 'Live-Verkäufe', end: false },
  { path: '/sales', label: 'Verkaufsanalyse', end: false },
  { path: '/temu-categories', label: 'Temu Category-IDs', end: true },
] as const satisfies readonly NavigationItem[]

const navigationGroups: readonly NavigationGroup[] = [
  { id: 'cockpit', label: 'Cockpit', items: cockpitViews },
  { id: 'analysis', label: 'Analysen', items: analysisViews },
]

function isPathActive(pathname: string, item: NavigationItem): boolean {
  return item.end
    ? pathname === item.path
    : pathname === item.path || pathname.startsWith(item.path + '/')
}

function primaryLinkClass(isActive: boolean, isFocusLink: boolean): string {
  if (isActive) {
    return 'rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm dark:bg-blue-500'
  }

  if (isFocusLink) {
    return 'rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800 transition hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200 dark:hover:bg-blue-900'
  }

  return 'rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
}

function NavItemLabel({ item }: { readonly item: NavigationItem }): ReactNode {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{item.label}</span>
      {item.badge === undefined ? null : (
        <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:bg-blue-900 dark:text-blue-200">
          {item.badge}
        </span>
      )}
    </span>
  )
}

export function AppNavigation(): ReactNode {
  const location = useLocation()
  const [openGroupState, setOpenGroupState] = useState<{
    readonly id: string
    readonly pathname: string
  } | null>(null)
  const openGroupId = openGroupState?.pathname === location.pathname ? openGroupState.id : null

  return (
    <nav
      aria-label="Hauptnavigation"
      className="mt-3 border-t border-slate-200 pt-3 dark:border-slate-700"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        {primaryViews.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.end}
            onClick={() => {
              setOpenGroupState(null)
            }}
            className={({ isActive }) => primaryLinkClass(isActive, item.path === '/topics')}
          >
            <NavItemLabel item={item} />
          </NavLink>
        ))}

        <span
          className="mx-1 hidden h-6 w-px bg-slate-200 sm:block dark:bg-slate-700"
          aria-hidden="true"
        />

        {navigationGroups.map((group) => {
          const isOpen = openGroupId === group.id
          const isActive = group.items.some((item) => isPathActive(location.pathname, item))

          return (
            <div key={group.id} className="relative">
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={isOpen}
                onClick={() => {
                  setOpenGroupState((current) =>
                    current?.id === group.id && current.pathname === location.pathname
                      ? null
                      : { id: group.id, pathname: location.pathname },
                  )
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setOpenGroupState(null)
                  }
                }}
                className={
                  isActive
                    ? 'inline-flex items-center gap-2 rounded-lg bg-slate-200 px-3 py-2 text-sm font-semibold text-slate-900 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600'
                    : 'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
                }
              >
                <span>{group.label}</span>
                {isOpen ? (
                  <ChevronUp aria-hidden="true" size={15} strokeWidth={1.8} />
                ) : (
                  <ChevronDown aria-hidden="true" size={15} strokeWidth={1.8} />
                )}
              </button>

              {isOpen ? (
                <div
                  role="group"
                  aria-label={`${group.label} Untermenü`}
                  className="absolute left-0 top-full z-20 mt-1 min-w-60 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-800"
                >
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.end}
                      onClick={() => {
                        setOpenGroupState(null)
                      }}
                      className={({ isActive: itemIsActive }) =>
                        itemIsActive
                          ? 'block rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-800 dark:bg-blue-950 dark:text-blue-200'
                          : 'block rounded-lg px-3 py-2 text-sm text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700'
                      }
                    >
                      <NavItemLabel item={item} />
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        Die Keyword-Bereiche und der Potenzial-Trichter stehen aktuell im Arbeitsfokus. Die übrigen
        Bereiche sind thematisch gebündelt.
      </p>
    </nav>
  )
}
